/**
 * Shared hook for importing files into a conversation.
 * Used by ChatBox paperclip and ConversationView drag-drop.
 */

import { useCallback, useRef, useState } from 'react';
import { env } from '@/lib/env';
import { api } from '@/lib/api';
import { useSession } from '@/features/auth/hooks/useSession';
import { getActiveWorkspaceId } from '@/lib/api/workspace-context';
import { getOriginalFetch, isNative } from '@/lib/capacitor';

export interface ImportSummary {
  totalRecords: number;
  importedRecords: number;
  failedRecords?: number;
  importedByType?: Record<string, number>;
}

export interface ImportFileStatus {
  id: string;
  filename: string;
  status: 'pending' | 'parsing' | 'parsed' | 'importing' | 'done' | 'error';
  format?: string | null;
  objectType?: string | null;
  recordCount?: number;
  importedCount?: number;
  error?: string | null;
}

export type ImportPhase = 'idle' | 'uploading' | 'parsing' | 'executing' | 'complete' | 'error';

export interface ImportResult {
  summary: ImportSummary;
  files: ImportFileStatus[];
}

interface UseImportOptions {
  conversationId: string;
  workspaceId?: string;
  onProgress?: (phase: ImportPhase, progress: { imported: number; total: number }) => void;
  onComplete?: (result: ImportResult) => void;
  onError?: (error: string) => void;
}

// File size limits by extension (in bytes)
const FILE_SIZE_LIMITS: Record<string, number> = {
  '.xlsx': 10 * 1024 * 1024,
  '.xls': 10 * 1024 * 1024,
  '.docx': 20 * 1024 * 1024,
  '.doc': 20 * 1024 * 1024,
};
const DEFAULT_SIZE_LIMIT = 50 * 1024 * 1024;

function getFileExtension(filename: string): string {
  const lastDot = filename.lastIndexOf('.');
  return lastDot >= 0 ? filename.slice(lastDot).toLowerCase() : '';
}

export function useImport({ conversationId, workspaceId, onProgress, onComplete, onError }: UseImportOptions) {
  const { token } = useSession();
  const resolvedWorkspaceId = workspaceId || getActiveWorkspaceId();
  const [phase, setPhase] = useState<ImportPhase>('idle');
  const [files, setFiles] = useState<ImportFileStatus[]>([]);
  const [progress, setProgress] = useState({ imported: 0, total: 0 });
  const [error, setError] = useState<string | null>(null);

  const filesRef = useRef<ImportFileStatus[]>([]);
  const progressRef = useRef({ imported: 0, total: 0 });
  const phaseRef = useRef<ImportPhase>('idle');
  // Guard against concurrent imports (React StrictMode can trigger double renders)
  const isImportingRef = useRef(false);

  const updatePhase = useCallback((newPhase: ImportPhase) => {
    phaseRef.current = newPhase;
    setPhase(newPhase);
  }, []);

  const updateFiles = useCallback((nextFiles: ImportFileStatus[]) => {
    filesRef.current = nextFiles;
    setFiles(nextFiles);
  }, []);

  const updateProgress = useCallback((next: { imported: number; total: number }) => {
    progressRef.current = next;
    setProgress(next);
    onProgress?.(phaseRef.current, next);
  }, [onProgress]);

  const reset = useCallback(() => {
    updatePhase('idle');
    updateFiles([]);
    setProgress({ imported: 0, total: 0 });
    progressRef.current = { imported: 0, total: 0 };
    setError(null);
    isImportingRef.current = false;
  }, [updatePhase, updateFiles]);

  const executeImport = useCallback(async (sessionId: string) => {
    updatePhase('executing');

    // Bypass CapacitorHttp on native (which buffers full responses) so SSE
    // streams via the WKWebView's native ReadableStream support.
    const sseFetch = isNative() ? getOriginalFetch() : fetch;
    const response = await sseFetch(`${env.apiBaseUrl}/workspaces/${getActiveWorkspaceId()}/import/${sessionId}/execute`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ conversationId, workspaceId: resolvedWorkspaceId }),
      credentials: isNative() ? 'omit' : 'include',
    });

    if (!response.ok || !response.body) {
      const errorText = await response.text().catch(() => 'No response body');
      throw new Error(`Import execution failed: ${response.status} ${response.statusText}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      let boundary = buffer.indexOf('\n\n');

      while (boundary !== -1) {
        const chunk = buffer.slice(0, boundary).trim();
        buffer = buffer.slice(boundary + 2);
        boundary = buffer.indexOf('\n\n');

        if (!chunk) continue;
        const lines = chunk.split('\n');
        let eventName = 'message';
        let payload = '';

        for (const line of lines) {
          if (line.startsWith('event:')) {
            eventName = line.replace('event:', '').trim();
          }
          if (line.startsWith('data:')) {
            payload += line.replace('data:', '').trim();
          }
        }

        if (!payload) continue;
        const data = JSON.parse(payload);

        if (eventName === 'progress') {
          updateProgress({ imported: data.imported ?? 0, total: data.total ?? 0 });
        }

        if (eventName === 'file-progress') {
          const fileId = data.fileId;
          const fileStatus = data.status as 'importing' | 'done' | 'error';
          const imported = data.imported ?? 0;
          const total = data.total ?? 0;

          const nextFiles = filesRef.current.map(f => {
            if (f.id !== fileId) return f;
            return { ...f, status: fileStatus, importedCount: imported, recordCount: total };
          });
          updateFiles(nextFiles);
        }

        if (eventName === 'complete') {
          updatePhase('complete');
          isImportingRef.current = false;
          const summary: ImportSummary = {
            totalRecords: data.totalRecords ?? progressRef.current.total ?? 0,
            importedRecords: data.importedRecords ?? data.totalRecords ?? progressRef.current.imported ?? 0,
            failedRecords: data.failedRecords ?? 0,
            importedByType: data.importedByType ?? {},
          };
          onComplete?.({ summary, files: filesRef.current });
        }

        if (eventName === 'error') {
          updatePhase('error');
          isImportingRef.current = false;
          setError(data.message || 'Import failed');
          onError?.(data.message || 'Import failed');
        }
      }
    }

    // Handle stream ending without complete event
    if (phaseRef.current === 'executing' && progressRef.current.imported > 0) {
      updatePhase('complete');
      isImportingRef.current = false;
      const summary: ImportSummary = {
        totalRecords: progressRef.current.total || progressRef.current.imported,
        importedRecords: progressRef.current.imported,
        failedRecords: 0,
        importedByType: {},
      };
      onComplete?.({ summary, files: filesRef.current });
    }
    // Ensure ref is reset even if stream ends unexpectedly
    isImportingRef.current = false;
  }, [conversationId, resolvedWorkspaceId, token, updatePhase, updateFiles, updateProgress, onComplete, onError]);

  const importFiles = useCallback(async (incoming: FileList | File[]) => {
    // Guard against concurrent imports (React StrictMode double-render protection)
    if (isImportingRef.current) {
      console.debug('[useImport] Import already in progress, skipping duplicate call');
      return;
    }
    isImportingRef.current = true;

    if (!token) {
      setError('Please log in to import files.');
      updatePhase('error');
      onError?.('Please log in to import files.');
      isImportingRef.current = false;
      return;
    }

    // Filter out hidden files and system files
    const fileList = Array.from(incoming).filter(file => {
      const basename = file.name.split('/').pop() || file.name;
      if (basename.startsWith('.')) return false;
      if (basename === 'Thumbs.db' || basename === 'desktop.ini') return false;
      return true;
    });

    if (fileList.length === 0) {
      isImportingRef.current = false;
      return;
    }

    // Validate file sizes
    const oversizedFiles: string[] = [];
    const validFiles: File[] = [];

    for (const file of fileList) {
      const ext = getFileExtension(file.name);
      const limit = FILE_SIZE_LIMITS[ext] ?? DEFAULT_SIZE_LIMIT;
      if (file.size > limit) {
        oversizedFiles.push(file.name);
      } else {
        validFiles.push(file);
      }
    }

    if (oversizedFiles.length > 0 && validFiles.length === 0) {
      const msg = `Files too large: ${oversizedFiles.join(', ')}`;
      setError(msg);
      updatePhase('error');
      onError?.(msg);
      isImportingRef.current = false;
      return;
    }

    if (validFiles.length === 0) {
      isImportingRef.current = false;
      return;
    }

    setError(null);
    updatePhase('uploading');
    updateFiles(validFiles.map(file => ({
      id: crypto.randomUUID(),
      filename: file.name,
      status: 'pending',
    })));

    try {
      const data = await api.assets.uploadImportFiles({
        workspaceId: getActiveWorkspaceId(),
        conversationId,
        bodyWorkspaceId: resolvedWorkspaceId,
        files: validFiles,
        token,
      });

      updatePhase('parsing');
      const parsedFiles: ImportFileStatus[] = (data.files || []).map((file): ImportFileStatus => ({
        id: file.id,
        filename: file.filename,
        status: file.status === 'error' ? 'error' : 'parsed',
        format: file.format,
        objectType: file.objectType,
        recordCount: file.recordCount ?? 0,
        error: file.error ?? null,
      }));
      updateFiles(parsedFiles);
      updateProgress({ imported: 0, total: data.totalRecords ?? 0 });

      await executeImport(data.sessionId);
    } catch (err) {
      updatePhase('error');
      const msg = (err as Error).message || 'Import failed';
      setError(msg);
      onError?.(msg);
      isImportingRef.current = false;
    }
  }, [conversationId, resolvedWorkspaceId, token, updatePhase, updateFiles, updateProgress, executeImport, onError]);

  return {
    importFiles,
    reset,
    phase,
    files,
    progress,
    error,
    isImporting: phase !== 'idle' && phase !== 'complete' && phase !== 'error',
  };
}
